import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, TimePicker, Space, Input, message } from 'antd';
import { useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { PickupAppointmentType } from '../graphql/queries/getPickupAppointmentListing';
import { MERGE_PICKUP_APPOINTMENTS, MergePickupAppointmentInput, MergePickupAppointmentResponse } from '../graphql/mutations/mergePickupAppointment';
import { useCooperationWarehouseSearch } from '../hooks/useCooperationWarehouseSearch';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

interface PickupMergeAppointmentModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    selectedAppointments: PickupAppointmentType[];
    pickupDate: string; // 从筛选项传入的揽件日期
}

interface MergeFormData {
    cooperationWarehouseId: number;
    expectedPickupTimeStart: Dayjs;
    expectedPickupTimeEnd: Dayjs;
    expectedParcelCount: number;
    expectedParcelUnit: string;
}

// 从类型定义文件中导入单位选项
const packageUnitFilterOptions = [
    {value: 'PIECE', label: 'PIECE'},
    {value: 'BAG', label: 'BAG'},
    {value: 'CARTON', label: 'CARTON'},
    {value: 'PALLET', label: 'PALLET'}
];

const PickupMergeAppointmentModal: React.FC<PickupMergeAppointmentModalProps> = ({
    visible,
    onClose,
    onSuccess,
    selectedAppointments,
    pickupDate
}) => {
    const [form] = Form.useForm<MergeFormData>();
    const [loading, setLoading] = useState(false);
    const {t} = useTranslation();
    // 合作仓库搜索功能
    const {
        options: cooperationWarehouseOptions,
        loading: cooperationWarehouseLoading,
        search: searchCooperationWarehouses,
        reset: resetCooperationWarehouseSearch
    } = useCooperationWarehouseSearch();

    // 计算当选中预约单都来自同一 Pickup Site 时的默认 Option（含 label），用于在下拉中展示正确的名称
    const samePickupSiteOption = React.useMemo(() => {
        if (!selectedAppointments || selectedAppointments.length === 0) return null;
        const firstId = selectedAppointments[0].cooperationWarehouseId;
        const allSame = selectedAppointments.every(appt => appt.cooperationWarehouseId === firstId);
        if (!firstId || !allSame) return null;
        const firstName = selectedAppointments[0].cooperationWarehouseName || '';
        return {
            cooperationWarehouseId: firstId,
            cooperationWarehouseName: firstName
        };
    }, [selectedAppointments]);

    // 合并下拉选项：若默认站点不在搜索结果里，则将其追加，确保能显示 label
    const displayCooperationWarehouseOptions = React.useMemo(() => {
        if (!samePickupSiteOption) return cooperationWarehouseOptions;
        const exists = cooperationWarehouseOptions.some(
            w => w.cooperationWarehouseId === samePickupSiteOption.cooperationWarehouseId
        );
        return exists ? cooperationWarehouseOptions : [samePickupSiteOption, ...cooperationWarehouseOptions];
    }, [cooperationWarehouseOptions, samePickupSiteOption]);

    // 合并预约的mutation
    const [mergeAppointments] = useMutation<MergePickupAppointmentResponse, { input: MergePickupAppointmentInput }>(MERGE_PICKUP_APPOINTMENTS);

    // 重置表单
    useEffect(() => {
        if (visible) {
            form.resetFields();
            // 重置搜索状态
            resetCooperationWarehouseSearch();
            // 设置默认的期望揽件时间
            form.setFieldsValue({
                expectedPickupTimeStart: dayjs().hour(9).minute(0).second(0),
                expectedPickupTimeEnd: dayjs().hour(18).minute(0).second(0),
                expectedParcelCount: 1,
                expectedParcelUnit: 'PIECE'
            });

            // 当弹窗打开时，如果所选预约单拥有相同的 Pickup Site，则默认选中该 Pickup Site
            if (selectedAppointments && selectedAppointments.length > 0) {
                const firstCooperationWarehouseId = selectedAppointments[0].cooperationWarehouseId;
                const isSamePickupSite = selectedAppointments.every(
                    appt => appt.cooperationWarehouseId === firstCooperationWarehouseId
                );
                if (firstCooperationWarehouseId && isSamePickupSite) {
                    form.setFieldsValue({ cooperationWarehouseId: firstCooperationWarehouseId });
                }
            }
        }

    }, [visible, form, resetCooperationWarehouseSearch]);

    const handleSubmit = async (values: MergeFormData) => {
        if (selectedAppointments.length < 2) {
            message.error('Please select at least 2 appointments to merge');
            return;
        }

                setLoading(true);
        try {
            // 构建合并预约的输入参数
            const mergeInput: MergePickupAppointmentInput = {
                appointmentIds: selectedAppointments.map(appointment => appointment.id),
                cooperationWarehouseId: values.cooperationWarehouseId,
                pickupDate: pickupDate,
                expectedPickupTimeStart: values.expectedPickupTimeStart.format('HH:mm'),
                expectedPickupTimeEnd: values.expectedPickupTimeEnd.format('HH:mm'),
                expectedParcelCount: Number(values.expectedParcelCount),
                expectedParcelUnit: values.expectedParcelUnit
            };

            // 调用后端合并接口
            await mergeAppointments({
                variables: { input: mergeInput }
            });

            message.success(`Successfully merged ${selectedAppointments.length} appointments into one`);
            onSuccess();
            onClose();

        } catch (error: any) {
            console.error('Merge appointments error:', error);
            message.error(`Failed to merge appointments: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            title={t('pickup.appointment.mergeAppointmentModal.title')}
            open={visible}
            onOk={() => form.submit()}
            onCancel={handleCancel}
            okText={t('pickup.appointment.mergeAppointmentModal.ok')}
            cancelText={t('pickup.appointment.mergeAppointmentModal.cancel')}
            confirmLoading={loading}
            width={600}
            destroyOnClose
        >
            <div className="mb-4 p-4 bg-gray-50 rounded">
                <div className="text-sm text-gray-600 mb-2">
                    {t('pickup.appointment.mergeAppointmentModal.appointmentSheetsToBeMerged', {count: selectedAppointments.length})}
                </div>
                <div className="space-y-1">
                    {selectedAppointments.map(appointment => (
                        <div key={appointment.id} className="text-sm">
                            <span className="font-medium">{appointment.appointmentNumber}</span>
                            <span className="text-gray-500 ml-2">
                                - {appointment.expectedParcelCount} {appointment.expectedParcelUnit}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    name="cooperationWarehouseId"
                    label={t('pickup.appointment.mergeAppointmentModal.pickupSite')}
                    rules={[{ required: true, message: 'Please select a Pickup Site.' }]}
                >
                    <Select
                        placeholder={t('pickup.appointment.mergeAppointmentModal.searchForPickupSites')}
                        showSearch
                        filterOption={false}
                        loading={cooperationWarehouseLoading}
                        notFoundContent={cooperationWarehouseLoading ? 'Loading...' : 'No data'}
                        onSearch={searchCooperationWarehouses}
                        onFocus={() => resetCooperationWarehouseSearch()}
                        allowClear
                        onClear={() => resetCooperationWarehouseSearch()}
                    >
                        {displayCooperationWarehouseOptions.map((warehouse) => (
                            <Option key={warehouse.cooperationWarehouseId} value={warehouse.cooperationWarehouseId}>
                                {warehouse.cooperationWarehouseName}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item label={t('pickup.appointment.mergeAppointmentModal.pickupDate')}>
                    <Input value={pickupDate} disabled className="bg-gray-50" />
                </Form.Item>

                <Form.Item label={t('pickup.appointment.mergeAppointmentModal.expectedTime')}>   
                    <Space.Compact block>
                        <Form.Item
                            name="expectedPickupTimeStart"
                            noStyle
                            rules={[{ required: true, message: 'Please select the start time.' }]}
                        >
                            <TimePicker 
                                style={{ width: '45%' }} 
                                format="HH:mm"
                                placeholder="start time"
                                onChange={() => {
                                    // 当开始时间改变时，重新验证结束时间
                                    form.validateFields(['expectedPickupTimeEnd']);
                                }}
                            />
                        </Form.Item>
                        <Input
                            style={{ width: '10%', textAlign: 'center' }}
                            placeholder="~"
                            disabled
                            className="bg-gray-50"
                        />
                        <Form.Item
                            name="expectedPickupTimeEnd"
                            noStyle
                            rules={[
                                { required: true, message: 'Please select the end time.' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const startTime = getFieldValue('expectedPickupTimeStart');
                                        if (!value || !startTime) {
                                            return Promise.resolve();
                                        }
                                        if (value.isAfter(startTime)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('The end time must be later than the start time.'));
                                    },
                                }),
                            ]}
                        >
                            <TimePicker 
                                style={{ width: '45%' }} 
                                format="HH:mm"
                                placeholder="end time"
                            />
                        </Form.Item>
                    </Space.Compact>
                </Form.Item>
                
                {/* 添加Expected Count输入框 */}
                <Form.Item
                    name="expectedParcelCount"
                    label={t('pickup.appointment.mergeAppointmentModal.expectedCount')}
                    rules={[{ required: true, message: 'Please enter the expected count.' }]}
                >
                    <Input type="number" min={1} placeholder={t('pickup.appointment.mergeAppointmentModal.enterExpectedCount') || undefined}   />
                </Form.Item>

                {/* 添加Expected Unit选择框 */}
                <Form.Item
                    name="expectedParcelUnit"
                    label={t('pickup.appointment.mergeAppointmentModal.expectedUnit')}
                    rules={[{ required: true, message: 'Please select the expected unit.' }]}
                >
                    <Select placeholder={t('pickup.appointment.mergeAppointmentModal.selectExpectedUnit')}>
                        {packageUnitFilterOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PickupMergeAppointmentModal;